// Franja única de filtros activos.
//
// Separa dos cosas que antes iban mezcladas: los CONTROLES para añadir filtros
// —que no se mueven nunca— y los filtros ACTIVOS, que viven aquí. Los chips
// colgando bajo cada campo hacían crecer la barra, desplazaban el resto de
// controles y daban la sensación de que la interfaz se recolocaba sola cada vez
// que se elegía algo.
//
// Ocupa UNA línea. A partir del cuarto chip el resto se resume en «+N», que
// abre un popover donde se pueden inspeccionar y quitar uno a uno. Así la altura
// de la pantalla es la misma con un filtro que con ocho.
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { usePosicionOverlay } from './usePosicionOverlay'

export interface FiltroActivo {
  clave: string
  /** Campo del que viene, cuando hay más de uno y podría haber ambigüedad.
   *  En un buscador global se omite: todos los términos son del mismo sitio. */
  campo?: string
  /** Identificador CORTO. La descripción larga va en `tooltip`, no aquí: un
   *  chip con «ARF32101314 CLUB DE NUIT WOMEN 105ML» se come la franja. */
  valor: string
  /** Descripción completa, al pasar el ratón. */
  tooltip?: string
  quitar: () => void
  /**
   * Los términos uno a uno, cuando el filtro trae muchos.
   *
   * Existe por el pegado: quien trae diez referencias de una columna de Excel
   * llenaba la franja con diez chips y empujaba fuera todo lo demás. Con esto el
   * chip RESUME («Artículo · 10 valores») y al pulsarlo se abren aquí, cada uno
   * con su X, para poder quitar sólo el que sobra sin reescribir la lista.
   */
  items?: ItemFiltro[]
}

export interface ItemFiltro {
  clave: string
  valor: string
  tooltip?: string
  quitar: () => void
}

/** Chips que se pintan antes de resumir el resto. */
const VISIBLES = 3

/** A partir de cuántos términos un filtro se resume en un solo chip.
 *
 *  Con dos, los chips sueltos se leen mejor y se quitan en un clic. Del tercero
 *  en adelante compensa resumir: es donde una lista pegada empieza a comerse la
 *  franja y a esconder los demás filtros. */
const RESUMIR_DESDE = 3

interface Props {
  dark: boolean
  filtros: FiltroActivo[]
  onLimpiar: () => void
  /** Feedback sobre lo que queda: «7 pedidos», «12 de 347 registros». */
  resumen?: string
}

export function BarraFiltrosActivos({ dark, filtros, onLimpiar, resumen }: Props) {
  const { t } = useTranslation()
  const btnMas = useRef<HTMLButtonElement>(null)
  const [abierto, setAbierto] = useState(false)
  // El mismo cálculo que los desplegables: un panel flotante no puede salirse de
  // la ventana ni ensancharla, y aquí además el botón está a la derecha del todo.
  const pos = usePosicionOverlay(btnMas, abierto, { anchoMaximo: 260, altoDeseado: 320 })

  if (filtros.length === 0) return null

  const visibles = filtros.slice(0, VISIBLES)
  const resto = filtros.slice(VISIBLES)

  const enlace = `text-[11px] font-medium cursor-pointer transition-colors duration-150 ${
    dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
  }`

  return (
    // `min-w-0` en la fila y en el contenedor de chips: sin él, un chip largo
    // impone su ancho mínimo al flex, el contenedor supera al padre por unos
    // píxeles y aparece la barra de scroll horizontal de 2-3 px.
    <div className="flex items-center gap-2 min-w-0 max-w-full">
      <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${
        dark ? 'text-slate-500' : 'text-slate-400'
      }`}>
        {t('common.filtros.activos')}
      </span>

      {/* Sin `flex-1`: el contenedor mide lo que miden sus chips, para que el
          «+N» y el «Limpiar todos» queden pegados al último y no al borde de la
          pantalla. `min-w-0` deja que encoja —y entonces scrollea— en vez de
          crecer hacia abajo: en estrecho la franja se desliza, pero la altura no
          cambia. */}
      <div className="flex items-center gap-1.5 min-w-0 overflow-x-auto overflow-y-hidden scroll-thin">
        {visibles.map(f => <Chip key={f.clave} dark={dark} filtro={f} />)}
      </div>

      {resto.length > 0 && (
        <button
          ref={btnMas}
          type="button"
          onClick={() => setAbierto(v => !v)}
          className={`shrink-0 h-[22px] px-2 rounded-md text-[11px] font-semibold border cursor-pointer transition-colors duration-150 ${
            dark
              ? 'border-white/10 text-slate-300 hover:bg-white/8'
              : 'border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
        >
          +{resto.length}
        </button>
      )}

      <button type="button" onClick={onLimpiar} className={`${enlace} shrink-0 hover:underline`}>
        {t('common.filtros.limpiarTodos')}
      </button>

      {resumen && (
        <span className={`ml-auto shrink-0 text-[11px] tabular-nums ${
          dark ? 'text-slate-400' : 'text-slate-500'
        }`}>
          {resumen}
        </span>
      )}

      {/* Portal: la franja vive dentro de tarjetas con `overflow-hidden`, así
          que un popover posicionado ahí dentro quedaría recortado. */}
      {abierto && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setAbierto(false)} aria-hidden="true" />
          <div
            className={`fixed z-[61] overflow-y-auto scroll-thin rounded-xl border shadow-2xl p-2 ${
              dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
            }`}
            style={{
              top: pos.top, left: pos.left, right: pos.right,
              width: pos.maxWidth, maxHeight: pos.maxHeight,
            }}
          >
            <p className={`px-1.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider ${
              dark ? 'text-slate-500' : 'text-slate-400'
            }`}>
              {t('common.filtros.activos')}
            </p>
            <ul className="flex flex-col gap-1">
              {resto.map(f => (
                <li key={f.clave}>
                  <Chip dark={dark} filtro={f} ancho />
                </li>
              ))}
            </ul>
          </div>
        </>,
        document.body,
      )}
    </div>
  )
}

function Chip({ dark, filtro, ancho }: { dark: boolean; filtro: FiltroActivo; ancho?: boolean }) {
  const { t } = useTranslation()
  const btn = useRef<HTMLButtonElement>(null)
  const [abierto, setAbierto] = useState(false)
  // El mismo cálculo de posición que el resto de flotantes de la casa.
  const pos = usePosicionOverlay(btn, abierto, { anchoMaximo: 280, altoDeseado: 320 })

  const items = filtro.items
  const texto = filtro.valor
    || (items ? t('common.filtros.nTerminos', { count: items.length }) : '')
  const etiqueta = (
    <span
      className="truncate whitespace-nowrap font-medium"
      title={filtro.tooltip ?? `${filtro.campo ?? ''} ${texto}`.trim()}
    >
      {texto}
    </span>
  )

  return (
    <span className={`flex items-center gap-1 h-[22px] pl-2 pr-0.5 rounded-md text-[11px] border ${
      ancho ? 'w-full' : 'max-w-[260px] shrink-0'
    } ${
      dark ? 'border-sky-500/25 bg-sky-500/10 text-sky-200' : 'border-sky-200 bg-sky-50 text-sky-800'
    }`}>
      {filtro.campo && (
        <span className={`shrink-0 whitespace-nowrap ${dark ? 'text-sky-400/70' : 'text-sky-600/70'}`}>
          {filtro.campo}
        </span>
      )}
      {items && items.length > 0 ? (
        <button
          ref={btn}
          type="button"
          onClick={() => setAbierto(a => !a)}
          aria-expanded={abierto}
          title={t('common.filtros.verTerminos')}
          className="flex items-center gap-0.5 min-w-0 cursor-pointer"
        >
          {etiqueta}
          <svg className={`w-2.5 h-2.5 shrink-0 transition-transform duration-150 ${abierto ? 'rotate-180' : ''}`}
               viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6.5l4 4 4-4" />
          </svg>
        </button>
      ) : etiqueta}

      {items && items.length > 0 && abierto && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[62]" onClick={() => setAbierto(false)} aria-hidden="true" />
          <div
            className={`fixed z-[63] overflow-y-auto scroll-thin rounded-xl border shadow-2xl p-2 ${
              dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
            }`}
            style={{ top: pos.top, left: pos.left, right: pos.right, width: pos.maxWidth, maxHeight: pos.maxHeight }}
          >
            <p className={`px-1.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider ${
              dark ? 'text-slate-500' : 'text-slate-400'
            }`}>
              {filtro.campo ?? t('common.filtros.activos')}
            </p>
            <ul className="flex flex-col gap-1">
              {items.map(it => (
                <li key={it.clave}>
                  {/* Quitar el último cierra el desplegable solo: el chip que lo
                      ancla deja de existir en cuanto el filtro se queda vacío. */}
                  <Chip
                    dark={dark}
                    ancho
                    filtro={{ clave: it.clave, valor: it.valor, tooltip: it.tooltip, quitar: it.quitar }}
                  />
                </li>
              ))}
            </ul>
          </div>
        </>,
        document.body,
      )}
      <button
        type="button"
        onClick={filtro.quitar}
        aria-label={`${t('common.clearFilters')}: ${texto}`}
        className={`flex items-center justify-center w-4 h-4 shrink-0 ml-auto rounded cursor-pointer transition-colors duration-150 ${
          dark ? 'hover:bg-sky-500/25' : 'hover:bg-sky-200/70'
        }`}
      >
        <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 2l6 6M8 2l-6 6" />
        </svg>
      </button>
    </span>
  )
}

/** Construye los filtros activos de un multi-selección.
 *
 *  Evita repetir en cada pantalla el mapeo selección → chips, que es donde se
 *  colarían diferencias de etiqueta entre unas y otras. */
export function filtrosDe(
  seleccion: string[],
  quitar: (valores: string[]) => void,
  opciones?: {
    campo?: string
    etiquetaDe?: (valor: string) => string
    /** Descripción larga para el tooltip: nunca entra en el chip. */
    tooltipDe?: (valor: string) => string | undefined
    prefijo?: string
  },
): FiltroActivo[] {
  const { campo, etiquetaDe, tooltipDe, prefijo = '' } = opciones ?? {}
  const items = seleccion.map(v => ({
    clave: `${prefijo}${v}`,
    valor: etiquetaDe?.(v) ?? v,
    tooltip: tooltipDe?.(v),
    quitar: () => quitar(seleccion.filter(x => x !== v)),
  }))
  if (items.length < RESUMIR_DESDE) return items.map(it => ({ ...it, campo }))
  // Muchos términos: UN chip que los resume y los despliega al pulsarlo. Su X
  // los quita todos de una vez, que es lo que se quiere tras pegar una lista
  // entera por error; para quitar sólo uno se abre y se usa la suya.
  return [{
    clave: `${prefijo}__resumen`,
    campo,
    // `valor` va vacío a propósito: lo rotula el Chip, que es un componente y
    // tiene el hook de traducción. Enhebrar una `t` por los doce sitios que
    // llaman aquí sería mucho ruido para una cuenta.
    valor: '',
    quitar: () => quitar([]),
    items,
  }]
}

/** Busca la descripción de un valor en un catálogo de opciones. */
export function descripcionDe(
  opciones: { valor: string; detalle?: string }[],
): (valor: string) => string | undefined {
  const mapa = new Map(opciones.map(o => [o.valor, o.detalle]))
  return v => mapa.get(v) || undefined
}
